import { memoize } from 'micro-memoize';

import { AccessControlResourcePatchRequest } from '@futrli-sdk/predict';
import { WorkspaceMember } from '@futrli-sdk/people';

import {
  AccessControlResourceResponseWithOwnerName,
  processResourcePermissions
} from '../../../processors/sharing/resourcePermissions';
import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import { resourcePermissionsQuery } from '../../../queries/sharing';
import { SharingEntityType } from '../../../../predict/src/core/api/sharing';
import { updateResourcePermissionsMutation } from '../../../mutations/sharing';
import { workspaceMembersQuery } from '../../../queries/members';

export interface SharingViewState extends ApiViewState {
  errorUpdating: Error | null;
  isUpdating: boolean;
  members: WorkspaceMember[];
  resourcePermissions?: AccessControlResourceResponseWithOwnerName;
}

export class SharingView extends ApiView {
  private resourcePermissionsObs: ReturnType<typeof resourcePermissionsQuery>;
  private workspaceMembersObs: ReturnType<typeof workspaceMembersQuery>;

  private updateResourcePermissionsMut: ReturnType<typeof updateResourcePermissionsMutation>;

  private memoizedProcessResourcePermissions: typeof processResourcePermissions;

  public state: SharingViewState = {
    ...getDefaultApiViewState(),
    errorUpdating: null,
    isUpdating: false,
    members: [],
    resourcePermissions: undefined
  };

  constructor(
    private workspaceId: string,
    private organisationId: string,
    private entityType: SharingEntityType,
    private entityId: string,
    private isConsolidation = false
  ) {
    super();

    this.resourcePermissionsObs = this.addQuery(
      resourcePermissionsQuery(workspaceId, organisationId, entityType, entityId, isConsolidation)
    );
    this.workspaceMembersObs = this.addQuery(workspaceMembersQuery(workspaceId));

    this.updateResourcePermissionsMut = this.addMutation(
      updateResourcePermissionsMutation(workspaceId, organisationId, entityType, entityId, isConsolidation)
    );

    this.memoizedProcessResourcePermissions = memoize(processResourcePermissions);

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************
  public resetMutations() {
    this.updateResourcePermissionsMut.reset();
    this.updateState();
  }

  public updateResourcePermissions(data: Partial<AccessControlResourcePatchRequest>) {
    return this.updateResourcePermissionsMut.mutate(data);
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************
  private get errorUpdating(): Error | null {
    return this.updateResourcePermissionsMut.getCurrentResult().error;
  }

  protected getNewState(): SharingViewState {
    return {
      ...super.getNewState(),
      errorUpdating: this.errorUpdating,
      isUpdating: this.isUpdating,
      members: this.members,
      resourcePermissions: this.resourcePermissions
    };
  }

  private get isUpdating(): boolean {
    return this.updateResourcePermissionsMut.getCurrentResult().status === 'pending';
  }

  private get members(): WorkspaceMember[] {
    return this.getResult(this.workspaceMembersObs).data ?? [];
  }

  private get resourcePermissions(): AccessControlResourceResponseWithOwnerName | undefined {
    const resourcePermissions = this.getResult(this.resourcePermissionsObs).data;
    const workspaceMembers = this.getResult(this.workspaceMembersObs).data;

    if (!resourcePermissions || !workspaceMembers) {
      return undefined;
    }

    return this.memoizedProcessResourcePermissions(resourcePermissions, workspaceMembers);
  }
}
